import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  orderBy,
  limit,
  startAfter,
  DocumentData,
  QueryDocumentSnapshot,
  enableNetwork,
  disableNetwork
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { InventoryItem, Category } from '../utils/types';
import { performanceCache, PerformanceUtils } from '../utils/performanceOptimizer';

// إعدادات التحسين
const OPTIMIZATION_CONFIG = {
  CACHE_TTL: 5 * 60 * 1000, // 5 دقائق
  MAX_BATCH_SIZE: 50,
  PREFETCH_SIZE: 20,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000
};

/**
 * خدمة محسنة للحصول على المنتجات مع تخزين مؤقت ذكي
 */
export class OptimizedStoreService {
  private static instance: OptimizedStoreService;
  private requestQueue = new Map<string, Promise<any>>();
  private prefetchQueue = new Set<string>();

  static getInstance(): OptimizedStoreService {
    if (!OptimizedStoreService.instance) {
      OptimizedStoreService.instance = new OptimizedStoreService();
    }
    return OptimizedStoreService.instance;
  }

  /**
   * الحصول على المنتجات مع تحسينات الأداء
   */
  async getOptimizedProducts(
    categoryId?: string,
    lastVisible?: QueryDocumentSnapshot<DocumentData>,
    itemsPerPage: number = 12,
    useCache: boolean = true
  ): Promise<{ products: InventoryItem[], lastVisible?: QueryDocumentSnapshot<DocumentData> }> {
    const cacheKey = `products_${categoryId || 'all'}_${lastVisible?.id || 'first'}_${itemsPerPage}`;

    try {
      // التحقق من التخزين المؤقت
      if (useCache) {
        const cached = performanceCache.get<{ products: InventoryItem[], lastVisible?: any }>(cacheKey);
        if (cached) {
          console.log('📦 Using cached products:', cacheKey);
          return cached;
        }
      }

      // التحقق من وجود طلب مماثل قيد التنفيذ
      if (this.requestQueue.has(cacheKey)) {
        console.log('⏳ Waiting for existing request:', cacheKey);
        return this.requestQueue.get(cacheKey);
      }

      // إنشاء طلب جديد
      const promise = this.executeProductQuery(categoryId, lastVisible, itemsPerPage);
      this.requestQueue.set(cacheKey, promise);

      const result = await promise;

      // حفظ في التخزين المؤقت
      if (useCache && result.products.length > 0) {
        performanceCache.set(cacheKey, result, OPTIMIZATION_CONFIG.CACHE_TTL);
      }

      // إزالة من قائمة الانتظار
      this.requestQueue.delete(cacheKey);

      // تحميل مسبق للصفحة التالية
      this.prefetchNextPage(categoryId, result.lastVisible, itemsPerPage);

      return result;
    } catch (error) {
      this.requestQueue.delete(cacheKey);
      throw error;
    }
  }

  /**
   * تنفيذ استعلام المنتجات مع إعادة المحاولة
   */
  private async executeProductQuery(
    categoryId?: string,
    lastVisible?: QueryDocumentSnapshot<DocumentData>,
    itemsPerPage: number = 12,
    attempt: number = 1
  ): Promise<{ products: InventoryItem[], lastVisible?: QueryDocumentSnapshot<DocumentData> }> {
    try {
      console.log(`🔍 Executing product query - Category: ${categoryId}, Attempt: ${attempt}`);

      let productsQuery;
      const collectionRef = collection(db, 'inventory');

      if (categoryId) {
        // التحقق من نوع الفئة
        const categoryDoc = await this.getCachedCategory(categoryId);

        if (categoryDoc) {
          const isSubcategory = categoryDoc.parentId !== undefined && categoryDoc.parentId !== null;

          if (isSubcategory) {
            productsQuery = query(
              collectionRef,
              where('subcategoryId', '==', categoryId),
              orderBy('createdAt', 'desc'),
              limit(itemsPerPage * 2) // نحصل على ضعف العدد للفلترة المحلية
            );
          } else {
            productsQuery = query(
              collectionRef,
              where('categoryId', '==', categoryId),
              orderBy('createdAt', 'desc'),
              limit(itemsPerPage * 2) // نحصل على ضعف العدد للفلترة المحلية
            );
          }
        } else {
          // فئة غير موجودة، استخدم الاستعلام الافتراضي
          productsQuery = query(
            collectionRef,
            where('showInStore', '==', true),
            orderBy('createdAt', 'desc'),
            limit(itemsPerPage)
          );
        }
      } else {
        // جميع المنتجات المعروضة في المتجر
        productsQuery = query(
          collectionRef,
          where('showInStore', '==', true),
          orderBy('createdAt', 'desc'),
          limit(itemsPerPage)
        );
      }

      // إضافة نقطة البداية للصفحة التالية
      if (lastVisible) {
        productsQuery = query(productsQuery, startAfter(lastVisible));
      }

      const snapshot = await getDocs(productsQuery);
      const lastVisibleDoc = snapshot.docs[snapshot.docs.length - 1];

      const products = snapshot.docs
        .map(doc => {
          const data = doc.data() as InventoryItem;
          return {
            ...data,
            id: doc.id,
            createdAt: this.parseDate(data.createdAt),
            updatedAt: this.parseDate(data.updatedAt)
          };
        })
        .filter(product => {
          // فلترة محلية للمنتجات المعروضة في المتجر (للفئات فقط)
          if (categoryId) {
            const showInStore = product.showInStore !== undefined ? product.showInStore : true;
            return showInStore === true;
          }
          return true; // للاستعلامات العامة، الفلترة تتم في الاستعلام نفسه
        })
        .slice(0, itemsPerPage); // تحديد العدد المطلوب بعد الفلترة

      console.log(`✅ Query successful - Found ${products.length} products`);
      return { products, lastVisible: lastVisibleDoc };

    } catch (error) {
      console.error(`❌ Query failed (attempt ${attempt}):`, error);

      // إعادة المحاولة مع تأخير متزايد
      if (attempt < OPTIMIZATION_CONFIG.RETRY_ATTEMPTS) {
        const delay = OPTIMIZATION_CONFIG.RETRY_DELAY * attempt;
        console.log(`⏳ Retrying in ${delay}ms...`);

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.executeProductQuery(categoryId, lastVisible, itemsPerPage, attempt + 1);
      }

      throw error;
    }
  }

  /**
   * الحصول على فئة مع تخزين مؤقت
   */
  private async getCachedCategory(categoryId: string): Promise<Category | null> {
    const cacheKey = `category_${categoryId}`;

    // التحقق من التخزين المؤقت
    const cached = performanceCache.get<Category>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const categoryDoc = await getDoc(doc(db, 'categories', categoryId));

      if (!categoryDoc.exists()) {
        return null;
      }

      const data = categoryDoc.data();
      const category: Category = {
        id: categoryDoc.id,
        name: data.name || 'فئة بدون اسم',
        description: data.description || '',
        parentId: data.parentId || undefined,
        imageUrl: data.imageUrl || undefined,
        isActive: data.isActive !== undefined ? data.isActive : true,
        order: data.order || 0,
        createdAt: this.parseDate(data.createdAt),
        updatedAt: this.parseDate(data.updatedAt),
        subCategories: []
      };

      // حفظ في التخزين المؤقت
      performanceCache.set(cacheKey, category, OPTIMIZATION_CONFIG.CACHE_TTL);

      return category;
    } catch (error) {
      console.error('Error fetching category:', error);
      return null;
    }
  }

  /**
   * تحميل مسبق للصفحة التالية
   */
  private prefetchNextPage(
    categoryId?: string,
    lastVisible?: QueryDocumentSnapshot<DocumentData>,
    itemsPerPage: number = 12
  ): void {
    if (!lastVisible) return;

    const prefetchKey = `prefetch_${categoryId || 'all'}_${lastVisible.id}`;

    if (this.prefetchQueue.has(prefetchKey)) {
      return;
    }

    this.prefetchQueue.add(prefetchKey);

    // تأخير التحميل المسبق لتجنب التحميل الزائد
    setTimeout(async () => {
      try {
        await this.getOptimizedProducts(categoryId, lastVisible, itemsPerPage, true);
        console.log('🚀 Prefetched next page successfully');
      } catch (error) {
        console.warn('⚠️ Prefetch failed:', error);
      } finally {
        this.prefetchQueue.delete(prefetchKey);
      }
    }, 2000);
  }

  /**
   * الحصول على الفئات مع تحسينات
   */
  async getOptimizedCategories(useCache: boolean = true): Promise<Category[]> {
    const cacheKey = 'categories_all';

    try {
      // التحقق من التخزين المؤقت
      if (useCache) {
        const cached = performanceCache.get<Category[]>(cacheKey);
        if (cached) {
          console.log('📦 Using cached categories');
          return cached;
        }
      }

      console.log('🔍 Fetching categories from database');
      const categoriesRef = collection(db, 'categories');
      const categoriesQuery = query(categoriesRef, orderBy('order', 'asc'));

      const snapshot = await getDocs(categoriesQuery);

      const allCategories: Category[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || 'فئة بدون اسم',
          description: data.description || '',
          parentId: data.parentId || undefined,
          imageUrl: data.imageUrl || undefined,
          isActive: data.isActive !== undefined ? data.isActive : true,
          order: data.order || 0,
          createdAt: this.parseDate(data.createdAt),
          updatedAt: this.parseDate(data.updatedAt),
          subCategories: []
        };
      });

      // تنظيم الفئات في هيكل شجري
      const organizedCategories = this.organizeCategories(allCategories);

      // حفظ في التخزين المؤقت
      if (useCache) {
        performanceCache.set(cacheKey, organizedCategories, OPTIMIZATION_CONFIG.CACHE_TTL);
      }

      console.log(`✅ Loaded ${organizedCategories.length} main categories`);
      return organizedCategories;

    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * تنظيم الفئات في هيكل شجري
   */
  private organizeCategories(categories: Category[]): Category[] {
    const mainCategories: Category[] = [];
    const subCategoriesMap: Record<string, Category[]> = {};

    // فصل الفئات الرئيسية والفرعية
    categories.forEach(category => {
      if (!category.parentId) {
        mainCategories.push(category);
      } else {
        if (!subCategoriesMap[category.parentId]) {
          subCategoriesMap[category.parentId] = [];
        }
        subCategoriesMap[category.parentId].push(category);
      }
    });

    // إضافة الفئات الفرعية إلى الفئات الرئيسية
    mainCategories.forEach(mainCategory => {
      if (subCategoriesMap[mainCategory.id]) {
        mainCategory.subCategories = subCategoriesMap[mainCategory.id]
          .sort((a, b) => (a.order || 0) - (b.order || 0));
      }
    });

    return mainCategories.sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  /**
   * البحث في المنتجات مع تحسينات
   */
  async searchProducts(
    searchQuery: string,
    categoryId?: string,
    limit: number = 20
  ): Promise<InventoryItem[]> {
    const cacheKey = `search_${searchQuery}_${categoryId || 'all'}_${limit}`;

    try {
      // التحقق من التخزين المؤقت
      const cached = performanceCache.get<InventoryItem[]>(cacheKey);
      if (cached) {
        console.log('📦 Using cached search results');
        return cached;
      }

      console.log(`🔍 Searching products: "${searchQuery}"`);

      // تحميل جميع المنتجات للفئة المحددة أو جميع المنتجات
      const { products } = await this.getOptimizedProducts(categoryId, undefined, 100, false);

      // تصفية النتائج محلياً
      const searchTerm = searchQuery.toLowerCase().trim();
      const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm) ||
        (product.description && product.description.toLowerCase().includes(searchTerm))
      ).slice(0, limit);

      // حفظ في التخزين المؤقت لفترة قصيرة
      performanceCache.set(cacheKey, filteredProducts, 2 * 60 * 1000); // دقيقتان

      console.log(`✅ Found ${filteredProducts.length} matching products`);
      return filteredProducts;

    } catch (error) {
      console.error('❌ Error searching products:', error);
      throw error;
    }
  }

  /**
   * مسح التخزين المؤقت
   */
  clearCache(pattern?: string): void {
    if (pattern) {
      // مسح مفاتيح محددة
      console.log(`🧹 Clearing cache with pattern: ${pattern}`);
    } else {
      // مسح جميع البيانات
      performanceCache.clear();
      console.log('🧹 Cleared all cache');
    }
  }

  /**
   * تحليل التاريخ مع معالجة أفضل للأخطاء
   */
  private parseDate(dateValue: any): Date {
    if (!dateValue) {
      return new Date();
    }

    if (dateValue instanceof Date) {
      return dateValue;
    }

    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }

    if (dateValue.seconds && dateValue.nanoseconds) {
      return new Date(dateValue.seconds * 1000 + dateValue.nanoseconds / 1000000);
    }

    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? new Date() : parsed;
    }

    return new Date();
  }

  /**
   * تفعيل/تعطيل الشبكة للاختبار
   */
  async toggleNetwork(enable: boolean): Promise<void> {
    try {
      if (enable) {
        await enableNetwork(db);
        console.log('🌐 Network enabled');
      } else {
        await disableNetwork(db);
        console.log('📴 Network disabled');
      }
    } catch (error) {
      console.error('❌ Error toggling network:', error);
    }
  }
}

// تصدير مثيل واحد
export const optimizedStoreService = OptimizedStoreService.getInstance();

// تصدير الوظائف المساعدة
export const StoreServiceUtils = {
  // تحديث التخزين المؤقت للمنتج
  updateProductCache: (productId: string, product: InventoryItem) => {
    const cacheKey = `product_${productId}`;
    performanceCache.set(cacheKey, product, OPTIMIZATION_CONFIG.CACHE_TTL);
  },

  // إزالة منتج من التخزين المؤقت
  removeProductFromCache: (productId: string) => {
    const cacheKey = `product_${productId}`;
    performanceCache.delete(cacheKey);
  },

  // تحديث التخزين المؤقت للفئة
  updateCategoryCache: (categoryId: string, category: Category) => {
    const cacheKey = `category_${categoryId}`;
    performanceCache.set(cacheKey, category, OPTIMIZATION_CONFIG.CACHE_TTL);
  }
};

export default optimizedStoreService;
